// ═══════════════════════════════════════════════════════════════
// V2 File Upload Utility
// Wraps the GAB V2 presigned URL upload flow.
//
// Flow:
//   1. POST /documents (JSON) → get attachmentId + presigned S3 URL
//   2. PUT file bytes through /api/s3upload proxy (server-side, no CORS)
//   3. Caller PATCHes the record's ATTACHMENT field with attachmentId
// ═══════════════════════════════════════════════════════════════

import { getTokens } from '../api/client';

const isDev = import.meta.env.DEV;
const DOC_API_BASE = isDev
  ? (import.meta.env.VITE_V2_API_BASE || 'https://gab-core-api.gab.ogintegration.us')
  : '/api/v2proxy';
const APP_ID = import.meta.env.VITE_V2_APP_ID || '8q39q8q5n';

/**
 * Upload a file to V2 native storage.
 * Step 1 gets a presigned S3 URL; step 2 routes the PUT through
 * the /api/s3upload Pages Function (server-side) to avoid CORS.
 * Returns the attachmentId (for linking to an ATTACHMENT-type record field)
 * and the documentId (for download-url requests).
 */
export async function uploadFileToV2(file: File, tableKey?: string): Promise<{ attachmentId: string; documentId: string }> {
  const tokenData = getTokens();
  if (!tokenData) throw new Error('Not authenticated');
  const { accessToken } = tokenData;

  // Step 1: Request upload slot → get presigned S3 URL
  const metaRes = await fetch(`${DOC_API_BASE}/v2/apps/${APP_ID}/documents`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(tableKey ? { tableKey } : {}),
      files: [{
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      }],
    }),
  });
  if (!metaRes.ok) {
    const body = await metaRes.text().catch(() => '');
    throw new Error(`Upload slot request failed (${metaRes.status}): ${body}`);
  }

  const { attachmentId, documents } = await metaRes.json() as {
    attachmentId: string;
    documents: Array<{ id: string; presignedUrl: string; method: string; headers: Record<string, string> }>;
  };
  const doc = documents[0];
  if (!doc) throw new Error('No document slot returned');

  // Step 2: PUT through server-side proxy to avoid S3 CORS restriction
  const form = new FormData();
  form.append('file', file);
  form.append('presignedUrl', doc.presignedUrl);
  if (doc.headers && Object.keys(doc.headers).length > 0) {
    form.append('headersJson', JSON.stringify(doc.headers));
  }

  const proxyRes = await fetch('/api/s3upload', {
    method: 'POST',
    body: form,
  });
  if (!proxyRes.ok) {
    const body = await proxyRes.text().catch(() => '');
    throw new Error(`File upload failed (${proxyRes.status}): ${body}`);
  }

  return { attachmentId, documentId: doc.id };
}

/**
 * Get a presigned download URL for a V2 document.
 * Polls up to `maxAttempts` times — V2 requires an async S3 event
 * confirmation before the download URL becomes available.
 */
export async function getV2FileDownloadUrl(documentId: string, maxAttempts = 3): Promise<string> {
  const tokenData = getTokens();
  if (!tokenData) throw new Error('Not authenticated');
  const { accessToken } = tokenData;

  let consecutive500 = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 1500)); // 1.5s between retries

    const res = await fetch(`${DOC_API_BASE}/v2/apps/${APP_ID}/documents/${documentId}/download-url`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.ok) {
      consecutive500 = 0;
      const data = await res.json() as { presignedUrl?: string; url?: string; error?: string };
      const downloadUrl = data.presignedUrl ?? data.url;
      if (downloadUrl) return downloadUrl;
      // API returned OK but no URL — might be transient, retry
      if (data.error && !data.error.includes('not yet confirmed')) {
        throw new Error(`Download URL error: ${data.error}`);
      }
    } else if (res.status === 500) {
      consecutive500++;
      // 2+ consecutive 500s means the file is likely not in storage — stop retrying
      if (consecutive500 >= 2) {
        throw new Error('FILE_UNAVAILABLE');
      }
    } else {
      // Non-500 errors (403, 404) won't resolve with retries
      throw new Error(`Failed to get download URL: ${res.status}`);
    }
  }

  throw new Error('FILE_UNAVAILABLE');
}
